import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Bluetooth, Cpu, Gauge, ShieldCheck, Network, Smartphone, MessageCircle, Sparkles, PackageCheck } from "lucide-react";
import { api } from "../lib/api";
import { useConfig } from "../lib/config";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";
import { isInStock, IN_STOCK_SLUGS } from "../lib/stock";

const features = [
  { icon: Cpu, title: "FOC Sinüs Sürüş", desc: "Frekans dönüşümlü FOC algoritması ile sessiz, hassas ve yüksek verimli motor sürüşü." },
  { icon: Bluetooth, title: "Mobil Uygulama", desc: "Android ve iOS uygulamaları üzerinden Bluetooth ile gerçek zamanlı tuning ve diagnostik." },
  { icon: Network, title: "CAN / 485 Haberleşme", desc: "Araç sistemlerine ve dijital göstergelere kolay entegrasyon için endüstri standardı protokoller." },
  { icon: ShieldCheck, title: "Çoklu Güvenlik Katmanı", desc: "Aşırı akım, voltaj, sıcaklık ve hız korumalı. Anti-theft modu ile güvende." },
  { icon: Gauge, title: "0 - 30 KW Aralık", desc: "Şehir içi günlük kullanımdan pist yarışı seviyesine kadar geniş ürün skalası." },
  { icon: Smartphone, title: "5 Sürüş Modu", desc: "Eco, Normal, Sport, Race ve özelleştirilebilir profillerle her sürücüye uygun." },
];

// İki spot ürün — stoğa gelen modeller
const HERO_STOCK = [
  { slug: "nd72360", name: "ND72360", spec: "72V · 360A · FOC", power: "2500W – 4000W", img: "/products/nd72360.jpg" },
  { slug: "nd72450", name: "ND72450", spec: "72V · 450A · FOC", power: "2800W – 4500W", img: "/products/ns72450.jpg" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const cfg = useConfig();

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  const waLink = cfg.whatsapp
    ? `https://wa.me/${cfg.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent("Merhaba, stoktaki Fardriver kontrolörü hakkında bilgi almak istiyorum.")}`
    : "/iletisim";

  // Öne çıkan listesinden stoktakileri çıkar — üstteki 2 büyük kartta zaten gösteriliyor
  const otherFeatured = featured.filter((p) => !IN_STOCK_SLUGS.has(p.slug));

  return (
    <div data-testid="home-page">
      <SEO
        title="Elektrikli Motor Beyin & Kontrolör Tedarikçisi"
        description="Fardriver Türkiye - ND72360 ve ND72450 modelleri Türkiye stoklarında! Elektrikli motosiklet, scooter ve ATV için orijinal Fardriver motor beyin (kontrolör) tedarikçisi."
        path="/"
      />

      {/* HERO — sınırlı stok teması */}
      <section className="relative border-b border-gray-200 overflow-hidden bg-white">
        <div className="absolute inset-0 fd-grid-bg opacity-60" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-10 md:pt-24 md:pb-12">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex flex-wrap items-center gap-2 mb-6"
            data-testid="hero-stock-ribbon"
          >
            <span className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              Yeni Geldi · Sınırlı Stok
            </span>
            <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono-fd text-gray-500">
              <Sparkles size={12} className="text-[#059669]" /> Türkiye Depo Teslim
            </span>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="font-display text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.92] tracking-tighter font-black"
              >
                Akımdan
                <br />
                <span className="text-[#059669]">harekete</span>.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="mt-7 max-w-xl text-base md:text-lg text-gray-700 leading-relaxed"
              >
                <strong className="text-black">ND72360</strong> ve <strong className="text-black">ND72450</strong> modelleri
                <span className="text-emerald-700 font-semibold"> Türkiye stoklarına ulaştı</span>. Sınırlı sayıda —
                stoklar tükenmeden bilgi alın.
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25 }}
                className="mt-8 flex flex-wrap gap-3"
              >
                <Link
                  to="/urunler"
                  data-testid="hero-cta-products"
                  className="inline-flex items-center gap-2 bg-[#059669] text-white px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-emerald-700 transition-all hover:-translate-y-0.5"
                >
                  Tüm Modeller <ArrowRight size={16} />
                </Link>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="hero-cta-whatsapp"
                  className="inline-flex items-center gap-2 bg-white border border-black text-black px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-black hover:text-white transition-all"
                >
                  <MessageCircle size={14} /> WhatsApp&apos;tan Sor
                </a>
              </motion.div>
            </div>

            {/* Sağ kolon — küçük bilgi kartı */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-5 lg:pt-4"
            >
              <div className="border border-gray-200 bg-white p-6">
                <p className="text-[10px] uppercase tracking-[0.22em] font-mono-fd text-[#059669]">Depo Durumu</p>
                <h3 className="font-display text-2xl tracking-tight mt-2">2 Model · Stokta</h3>
                <ul className="mt-5 space-y-2 text-sm">
                  {HERO_STOCK.map((h) => (
                    <li key={h.slug} className="flex items-center justify-between border-t border-gray-100 pt-2">
                      <Link to={`/urunler/${h.slug}`} className="font-display tracking-tight hover:text-[#059669]">
                        {h.name}
                      </Link>
                      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-semibold">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" style={{ boxShadow: "0 0 6px rgba(16,185,129,0.9)" }} />
                        Stokta
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Alt şerit */}
        <div className="relative border-t border-gray-200 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3 text-[10px] uppercase tracking-[0.22em] font-mono-fd text-gray-500">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
              <span>Türkiye Tedarikçisi</span>
            </div>
            <span className="hidden sm:inline">Orijinal Ürün</span>
            <span className="hidden sm:inline">Türkçe Destek</span>
            <span className="hidden sm:inline">Orijinal Ürün · Hızlı Kargo</span>
          </div>
        </div>
      </section>

      {/* İKİ SPOT ÜRÜN — Büyük yan yana kartlar */}
      <section className="border-b border-gray-200 bg-gradient-to-b from-emerald-50/40 to-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="mb-10">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#059669] mb-3 inline-flex items-center gap-2">
              <PackageCheck size={14} /> Stoktaki Modeller
            </p>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter font-black leading-tight">
              Stoktaki Modeller.
            </h2>
            <p className="mt-3 text-gray-600 max-w-xl">
              Aşağıdaki iki model şu an Türkiye deposunda — talep yoğunluğu nedeniyle stoklar sınırlıdır.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-testid="hero-stock-cards">
            {HERO_STOCK.map((h, i) => (
              <motion.div
                key={h.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                data-testid={`hero-stock-card-${h.slug}`}
                className="group relative border border-gray-200 bg-white overflow-hidden hover:border-emerald-300 transition-all hover:-translate-y-1 hover:shadow-2xl"
              >
                <Link to={`/urunler/${h.slug}`} className="block">
                  <span className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 bg-emerald-600 text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold shadow-lg">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                    Yeni Geldi
                  </span>

                  <div className="aspect-[5/4] bg-gray-50 flex items-center justify-center p-8 border-b border-gray-100">
                    <img
                      src={h.img}
                      alt={`${h.name} — Fardriver kontrolör stokta`}
                      width="800"
                      height="640"
                      loading="lazy"
                      className="max-w-[85%] max-h-[85%] object-contain drop-shadow-xl transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>

                  <div className="flex items-center gap-2 px-6 py-3 bg-emerald-50/70 border-b border-emerald-100">
                    <span className="relative flex h-3 w-3 items-center justify-center">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
                      <span
                        className="relative inline-block h-3 w-3 rounded-full bg-emerald-500"
                        style={{ boxShadow: "0 0 10px rgba(16,185,129,0.95)" }}
                      />
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
                      Stokta Var
                    </span>
                  </div>

                  <div className="p-6 md:p-7">
                    <p className="text-[10px] uppercase tracking-[0.22em] font-mono-fd text-[#059669]">Fardriver</p>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tighter font-black mt-1 group-hover:text-[#059669]">
                      {h.name}
                    </h3>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
                      <span><strong className="text-black">{h.spec}</strong></span>
                      <span>{h.power}</span>
                    </div>
                    <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
                      <span className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">
                        Detayları İncele
                      </span>
                      <span className="text-[#059669] text-lg font-semibold group-hover:translate-x-1 transition-transform">
                        →
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14 items-end">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#059669] mb-4">Neden Fardriver</p>
              <h2 className="font-display text-3xl md:text-5xl tracking-tighter font-black leading-tight">
                Mühendislik tutkulu sürüş için.
              </h2>
            </div>
            <p className="lg:col-span-5 text-gray-600 leading-relaxed">
              Fardriver kontrolörleri, otomotiv güvenlik elektroniği üretiminde uzmanlaşan markanın
              elektrikli motor beyin teknolojisi olarak şehir motosikletinden pist yarışına geniş bir
              ürün portföyü sunar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-gray-200">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="border-r border-b border-gray-200 p-8 hover:bg-gray-50 transition-colors"
                data-testid={`feature-${i}`}
              >
                <f.icon size={28} className="text-[#059669]" />
                <h3 className="font-display text-xl mt-5 tracking-tight">{f.title}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Diğer öne çıkan ürünler — stokta yok */}
      {otherFeatured.length > 0 && (
        <section className="border-b border-gray-200 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#059669] mb-4">Diğer Modeller</p>
                <h2 className="font-display text-3xl md:text-5xl tracking-tighter font-black">
                  Katalogdaki tüm seriler.
                </h2>
                <p className="mt-3 text-gray-600 max-w-xl text-sm">
                  Aşağıdaki modeller şu an stokta değil. Detayları inceleyip WhatsApp&apos;tan bilgi alabilirsiniz.
                </p>
              </div>
              <Link
                to="/urunler"
                data-testid="featured-view-all"
                className="text-sm font-semibold uppercase tracking-[0.15em] border-b-2 border-black hover:text-[#059669] hover:border-[#059669]"
              >
                Tümünü gör →
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {otherFeatured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA */}
    </div>
  );
}
